import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartArea, faChartColumn, faChartLine, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../config/api';
import { SurveyQuestion, SurveyQuestionType } from '../../types';
import { surveyAnswerColor } from './surveyUtils';

interface AnalyticsSurvey {
  _id: string;
  name: string;
  status?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  questionIds: string[];
}

interface AnalyticsAnswer {
  surveyId: string;
  surveyName: string;
  questionId: string;
  prompt: string;
  type: SurveyQuestionType;
  value: string;
  additionalInfo?: string;
  completedAt: string | Date;
}

interface AnalyticsPayload {
  surveys: AnalyticsSurvey[];
  questions: SurveyQuestion[];
  answers: AnalyticsAnswer[];
}

type TimeGroup = 'day' | 'week' | 'month';
type ChartType = 'share' | 'line' | 'area' | 'count';
type ChartScale = 'percent' | 'quantity';

const CHART_TYPE_OPTIONS: { value: ChartType; label: string; icon: typeof faChartLine }[] = [
  { value: 'share', label: 'Share', icon: faLayerGroup },
  { value: 'line', label: 'Line', icon: faChartLine },
  { value: 'area', label: 'Area', icon: faChartArea },
  { value: 'count', label: 'Counts', icon: faChartColumn },
];

const CHART_SCALE_OPTIONS: { value: ChartScale; label: string }[] = [
  { value: 'quantity', label: 'Quantity' },
  { value: 'percent', label: 'Percentage' },
];


const startOfDay = (value: Date) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const startOfWeek = (value: Date) => {
  const date = startOfDay(value);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date;
};

const startOfMonth = (value: Date) => {
  const date = startOfDay(value);
  date.setDate(1);
  return date;
};

const localDayKey = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatBucket = (key: string, group: TimeGroup) => {
  const date = new Date(`${key}T00:00:00`);
  if (Number.isNaN(date.getTime())) return key;
  if (group === 'month') {
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatRangeDate = (value?: string | Date | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const surveyRangeLabel = (
  survey: AnalyticsSurvey | undefined,
  answers: AnalyticsAnswer[]
) => {
  const name = survey?.name || answers[0]?.surveyName || 'Unknown survey';
  const dates = answers
    .map((answer) => new Date(answer.completedAt))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((left, right) => left.getTime() - right.getTime());
  const start = formatRangeDate(dates[0] || survey?.createdAt);
  if (!start) return { name, range: '' };
  const stillOpen = !survey?.status || survey.status === 'active' || survey.status === 'paused';
  const end = stillOpen
    ? 'Current'
    : (formatRangeDate(dates[dates.length - 1] || survey?.updatedAt) || 'Current');
  return { name, range: `(${start}-${end})` };
};

const formatAnswer = (value: string, type?: SurveyQuestionType) => {
  if (type === 'boolean') {
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
  }
  return value;
};

const toDateInput = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfInputDay = (value: string) => new Date(`${value}T00:00:00`);
const endOfInputDay = (value: string) => new Date(`${value}T23:59:59.999`);

const datePresetRange = (preset: '7d' | 'month' | '3m') => {
  const end = new Date();
  const start = new Date();
  if (preset === '7d') start.setDate(start.getDate() - 6);
  if (preset === 'month') start.setMonth(start.getMonth() - 1);
  if (preset === '3m') start.setMonth(start.getMonth() - 3);
  return { dateStart: toDateInput(start), dateEnd: toDateInput(end) };
};

const SurveyDashboardPage: React.FC = () => {
  const [data, setData] = useState<AnalyticsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [surveyId, setSurveyId] = useState('');
  const [questionId, setQuestionId] = useState('');
  const [timeGroup, setTimeGroup] = useState<TimeGroup>('day');
  const [chartType, setChartType] = useState<ChartType>('share');
  const [chartScale, setChartScale] = useState<ChartScale>('percent');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/surveys/analytics`, {
          withCredentials: true,
        });
        setData({
          surveys: response.data.surveys || [],
          questions: response.data.questions || [],
          answers: response.data.answers || [],
        });
        setError(null);
      } catch (err: any) {
        console.error('Error fetching survey analytics:', err);
        setError(err.response?.data?.message || 'Failed to load survey analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const questionsForFilter = useMemo(() => {
    const questions = data?.questions || [];
    if (!surveyId) {
      const answeredIds = new Set((data?.answers || []).map((answer) => answer.questionId));
      return questions.filter((question) => question._id && answeredIds.has(question._id));
    }
    const selected = data?.surveys.find((survey) => survey._id === surveyId);
    const allowed = new Set(selected?.questionIds || []);
    return questions.filter((question) => question._id && allowed.has(question._id));
  }, [data, surveyId]);

  useEffect(() => {
    if (!questionsForFilter.length) {
      if (questionId) setQuestionId('');
      return;
    }
    if (!questionId || !questionsForFilter.some((question) => question._id === questionId)) {
      setQuestionId(questionsForFilter[0]._id || '');
    }
  }, [questionsForFilter, questionId]);

  const selectedQuestion = questionsForFilter.find((question) => question._id === questionId);
  const filteredAnswers = useMemo(() => {
    const rangeStart = dateStart ? startOfInputDay(dateStart) : null;
    const rangeEnd = dateEnd ? endOfInputDay(dateEnd) : null;
    return (data?.answers || []).filter((answer) => {
      if (surveyId && answer.surveyId !== surveyId) return false;
      if (questionId && answer.questionId !== questionId) return false;
      const completedAt = new Date(answer.completedAt);
      if (Number.isNaN(completedAt.getTime())) return false;
      if (rangeStart && completedAt < rangeStart) return false;
      if (rangeEnd && completedAt > rangeEnd) return false;
      return true;
    });
  }, [data, dateEnd, dateStart, questionId, surveyId]);

  const values = useMemo(() => {
    const seen: string[] = [];
    filteredAnswers.forEach((answer) => {
      const label = formatAnswer(answer.value, selectedQuestion?.type);
      if (!seen.includes(label)) seen.push(label);
    });
    return seen;
  }, [filteredAnswers, selectedQuestion?.type]);

  const timeSeries = useMemo(() => {
    const buckets = new Map<string, Record<string, number | string>>();
    filteredAnswers.forEach((answer) => {
      const date = new Date(answer.completedAt);
      if (Number.isNaN(date.getTime())) return;
      const start = timeGroup === 'month'
        ? startOfMonth(date)
        : timeGroup === 'week'
          ? startOfWeek(date)
          : startOfDay(date);
      const key = localDayKey(start);
      const label = formatAnswer(answer.value, selectedQuestion?.type);
      const current = buckets.get(key) || { bucket: key, label: formatBucket(key, timeGroup) };
      current[label] = Number(current[label] || 0) + 1;
      buckets.set(key, current);
    });
    return Array.from(buckets.values())
      .sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)))
      .map((row) => {
        const filled: Record<string, number | string> = { ...row };
        values.forEach((value) => {
          if (filled[value] === undefined) filled[value] = 0;
        });
        return filled;
      });
  }, [filteredAnswers, selectedQuestion?.type, timeGroup, values]);

  const shareSeries = useMemo(() => {
    return timeSeries.map((row) => {
      let total = 0;
      values.forEach((value) => {
        total += Number(row[value] || 0);
      });
      const next: Record<string, number | string> = { ...row, total };
      values.forEach((value) => {
        const count = Number(row[value] || 0);
        next[`${value}Pct`] = total ? Math.round((count / total) * 1000) / 10 : 0;
      });
      return next;
    });
  }, [timeSeries, values]);

  const totals = useMemo(() => {
    return values.map((value) => ({
      name: value,
      count: filteredAnswers.filter((answer) => formatAnswer(answer.value, selectedQuestion?.type) === value).length,
    }));
  }, [filteredAnswers, selectedQuestion?.type, values]);

  const surveyBreakdown = useMemo(() => {
    const bySurvey = new Map<string, AnalyticsAnswer[]>();
    filteredAnswers.forEach((answer) => {
      const list = bySurvey.get(answer.surveyId) || [];
      list.push(answer);
      bySurvey.set(answer.surveyId, list);
    });
    return Array.from(bySurvey.entries()).map(([id, answers]) => {
      const survey = data?.surveys.find((entry) => String(entry._id) === String(id));
      const { name, range } = surveyRangeLabel(survey, answers);
      const row: Record<string, number | string> = { name, range };
      answers.forEach((answer) => {
        const label = formatAnswer(answer.value, selectedQuestion?.type);
        row[label] = Number(row[label] || 0) + 1;
      });
      return row;
    });
  }, [data?.surveys, filteredAnswers, selectedQuestion?.type]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  const isText = selectedQuestion?.type === 'text';
  const isBoolean = selectedQuestion?.type === 'boolean';
  const usePercent = chartScale === 'percent';
  const answerCount = filteredAnswers.length;
  const surveyCount = new Set(filteredAnswers.map((answer) => answer.surveyId)).size;
  const yesCount = filteredAnswers.filter((answer) => formatAnswer(answer.value, 'boolean') === 'Yes').length;
  const overallYesPct = answerCount ? Math.round((yesCount / answerCount) * 1000) / 10 : 0;
  const seriesKey = (value: string) => (usePercent ? `${value}Pct` : value);
  const formatPoint = (value: number, name: string, item: { payload?: Record<string, number | string> }) => {
    const count = Number(item.payload?.[name] || 0);
    return usePercent ? [`${value}% (${count})`, name] : [count, name];
  };

  return (
    <>
      <div className="card shadow-sm border-0 mb-3 survey-dashboard-filters">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-bold" htmlFor="survey-dashboard-survey">Survey</label>
              <select
                id="survey-dashboard-survey"
                className="form-select"
                value={surveyId}
                onChange={(e) => setSurveyId(e.target.value)}
              >
                <option value="">All surveys</option>
                {(data?.surveys || []).map((survey) => (
                  <option key={survey._id} value={survey._id}>{survey.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-8">
              <label className="form-label fw-bold" htmlFor="survey-dashboard-question">Question</label>
              <select
                id="survey-dashboard-question"
                className="form-select"
                value={questionId}
                onChange={(e) => setQuestionId(e.target.value)}
              >
                {questionsForFilter.length === 0 && <option value="">No questions with answers</option>}
                {questionsForFilter.map((question) => (
                  <option key={question._id} value={question._id}>
                    {question.prompt}
                    {question.usedOnSurveyCount && question.usedOnSurveyCount > 1
                      ? ` (${question.usedOnSurveyCount} surveys)`
                      : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold" htmlFor="survey-dashboard-start">Start date</label>
              <input
                id="survey-dashboard-start"
                type="date"
                className="form-control"
                value={dateStart}
                onChange={(event) => setDateStart(event.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold" htmlFor="survey-dashboard-end">End date</label>
              <input
                id="survey-dashboard-end"
                type="date"
                className="form-control"
                value={dateEnd}
                min={dateStart || undefined}
                onChange={(event) => setDateEnd(event.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold" htmlFor="survey-dashboard-group">Group by</label>
              <select
                id="survey-dashboard-group"
                className="form-select"
                value={timeGroup}
                onChange={(e) => setTimeGroup(e.target.value as TimeGroup)}
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Quick range</label>
              <div className="d-flex flex-wrap gap-2">
                {([
                  { id: '7d' as const, label: 'Last 7 days' },
                  { id: 'month' as const, label: 'Last month' },
                  { id: '3m' as const, label: 'Last 3 months' },
                ]).map((preset) => {
                  const range = datePresetRange(preset.id);
                  const active = dateStart === range.dateStart && dateEnd === range.dateEnd;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        setDateStart(range.dateStart);
                        setDateEnd(range.dateEnd);
                      }}
                    >
                      {preset.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setDateStart('');
                    setDateEnd('');
                  }}
                  disabled={!dateStart && !dateEnd}
                >
                  All time
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="text-muted text-uppercase small fw-bold">Question</div>
              <div className="fw-bold">{selectedQuestion?.prompt || '—'}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="text-muted text-uppercase small fw-bold">Surveys</div>
              <div className="fs-3 fw-bold">{surveyCount}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="text-muted text-uppercase small fw-bold">Answers</div>
              <div className="fs-3 fw-bold">{answerCount}</div>
            </div>
          </div>
        </div>
      </div>

      {!selectedQuestion || answerCount === 0 ? (
        <div className="deploy-test-card-empty">No answers match these filters</div>
      ) : isText ? (
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h5 className="mb-3">Written answers over time</h5>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Survey</th>
                    <th>Answer</th>
                  </tr>
                </thead>
                <tbody>
                  {[...filteredAnswers].reverse().map((answer, index) => (
                    <tr key={`${answer.surveyId}-${answer.completedAt}-${index}`}>
                      <td className="text-nowrap">
                        {new Date(answer.completedAt).toLocaleDateString()}
                      </td>
                      <td>{answer.surveyName}</td>
                      <td>{answer.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
                <div>
                  <h5 className="mb-1">{isBoolean ? 'Yes vs No over time' : 'Answers over time'}</h5>
                  <div className="text-muted small">
                    {usePercent
                      ? `Share of answers in each ${timeGroup}, so a change in mix is visible even when volume changes`
                      : `Raw answer counts by ${timeGroup}`}
                  </div>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-3">
                  {isBoolean ? (
                    <div className="text-muted small">
                      Overall <strong>{overallYesPct}% Yes</strong>
                    </div>
                  ) : null}
                  <div className="btn-group btn-group-sm" role="group" aria-label="Chart scale">
                    {CHART_SCALE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`btn ${chartScale === option.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setChartScale(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div className="btn-group btn-group-sm" role="group" aria-label="Chart type">
                    {CHART_TYPE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`btn ${chartType === option.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setChartType(option.value)}
                        title={option.label}
                      >
                        <FontAwesomeIcon icon={option.icon} className="me-1" />
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  {chartType === 'line' ? (
                    <LineChart data={shareSeries} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis
                        domain={usePercent ? [0, 100] : [0, 'auto']}
                        allowDecimals={!usePercent ? false : undefined}
                        tickFormatter={usePercent ? (value) => `${value}%` : undefined}
                      />
                      <Tooltip formatter={formatPoint} />
                      <Legend />
                      {values.map((value, index) => (
                        <Line
                          key={value}
                          type="monotone"
                          dataKey={seriesKey(value)}
                          name={value}
                          stroke={surveyAnswerColor(value, index)}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      ))}
                    </LineChart>
                  ) : chartType === 'area' ? (
                    <AreaChart data={shareSeries} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis
                        domain={usePercent ? [0, 100] : [0, 'auto']}
                        allowDecimals={!usePercent ? false : undefined}
                        tickFormatter={usePercent ? (value) => `${value}%` : undefined}
                      />
                      <Tooltip formatter={formatPoint} />
                      <Legend />
                      {values.map((value, index) => (
                        <Area
                          key={value}
                          type="monotone"
                          dataKey={seriesKey(value)}
                          name={value}
                          stackId="share"
                          stroke={surveyAnswerColor(value, index)}
                          fill={surveyAnswerColor(value, index)}
                          fillOpacity={0.75}
                        />
                      ))}
                    </AreaChart>
                  ) : chartType === 'count' ? (
                    <BarChart data={shareSeries} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis
                        domain={usePercent ? [0, 100] : [0, 'auto']}
                        allowDecimals={!usePercent ? false : undefined}
                        tickFormatter={usePercent ? (value) => `${value}%` : undefined}
                      />
                      <Tooltip formatter={formatPoint} />
                      <Legend />
                      {values.map((value, index) => (
                        <Bar
                          key={value}
                          dataKey={seriesKey(value)}
                          name={value}
                          fill={surveyAnswerColor(value, index)}
                        />
                      ))}
                    </BarChart>
                  ) : (
                    <BarChart data={shareSeries} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis
                        domain={usePercent ? [0, 100] : [0, 'auto']}
                        allowDecimals={!usePercent ? false : undefined}
                        tickFormatter={usePercent ? (value) => `${value}%` : undefined}
                      />
                      <Tooltip
                        formatter={formatPoint}
                        labelFormatter={(label, items) => {
                          const total = items?.[0]?.payload?.total;
                          return total ? `${label} · ${total} answer${Number(total) === 1 ? '' : 's'}` : String(label);
                        }}
                      />
                      <Legend />
                      {values.map((value, index) => (
                        <Bar
                          key={value}
                          dataKey={seriesKey(value)}
                          name={value}
                          stackId="share"
                          fill={surveyAnswerColor(value, index)}
                        />
                      ))}
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h5 className="mb-3">Answer totals</h5>
                  <div style={{ width: '100%', height: 320 }}>
                    <ResponsiveContainer>
                      <BarChart data={totals} margin={{ top: 36, right: 16, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" interval={0} height={48} tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count">
                          {totals.map((row, index) => (
                            <Cell key={row.name} fill={surveyAnswerColor(row.name, index)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h5 className="mb-3">By survey</h5>
                  <div style={{ width: '100%', height: 320 }}>
                    <ResponsiveContainer>
                      <BarChart data={surveyBreakdown} margin={{ top: 36, right: 16, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          interval={0}
                          height={48}
                          tick={(props) => {
                            const { x, y, payload } = props;
                            const row = surveyBreakdown.find((entry) => entry.name === payload.value);
                            return (
                              <g transform={`translate(${x},${y})`}>
                                <text x={0} y={0} dy={12} textAnchor="middle" fontSize={12} fontWeight={700} fill="#111827">
                                  {payload.value}
                                </text>
                                {row?.range ? (
                                  <text x={0} y={0} dy={28} textAnchor="middle" fontSize={11} fill="#6b7280">
                                    {row.range}
                                  </text>
                                ) : null}
                              </g>
                            );
                          }}
                        />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend verticalAlign="top" height={28} />
                        {values.map((value, index) => (
                          <Bar
                            key={value}
                            dataKey={value}
                            stackId="answers"
                            fill={surveyAnswerColor(value, index)}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SurveyDashboardPage;
